import React, { useEffect, useState } from "react";
import LoadingBar from "react-top-loading-bar";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import { AdminPageTemplate, ColumnToolTip } from "../../features";
import { ExportTableType, PaginationSettings } from "../../configs";
import { useAppSelector, VRIAppStateType } from "../../core";
import { ApiSchemaColumn, ApiSchemaFilter, TableColumnExtend } from "../../models";
import { useFunctionalityLanguageSpokenTerms } from "../hooks";
import { dateFormatInUSA, timeFormatterInAMPM } from "../../utils";

type SpokenTermRow = {
  callSid: string;
  spokenTerms: string;
  dateTime: string;
};

function stripDateTime(value: any): string {
  if (value == null) return "";

  const text = typeof value === "string" ? value : String(value);
  const dateRegex =
    /\d{1,2}\/\d{1,2}\/\d{4}\s+\d{1,2}:\d{2}:\d{2}\s*(AM|PM)?/gi;

  return text
    .replace(dateRegex, " - ")
    .replace(/[.,]/g, "")
    .replace(/\s*-\s*/g, " - ")
    .replace(/(?:\s-\s)+/g, " - ")
    .replace(/\s*-\s*$/, "")
    .replace(/\s+/g, " ")
    .trim();
}

function formatDateTimeOrDash(dateTime: string): string {
  if (!dateTime || dateTime === "0001-01-01T00:00:00") {
    return "-";
  }

  return `${dateFormatInUSA(dateTime)} ${timeFormatterInAMPM(dateTime)}`;
}

export function PageSpokenTermsPerCall() {
  const [pageIndex, setPageIndex] = useState(
    PaginationSettings.initialPageIndex
  );
  const [pageSize, setPageSize] = useState(
    PaginationSettings.initialPageSize
  );
  const [searchText, setSearchText] = useState<string>("");
  const [hasPagingData, setHasPagingData] = useState<boolean>(false);
  const [loaderProgress, setLoaderProgress] = useState<number>(0);
  const [isShowFilter, setIsShowFilter] = useState<boolean>(false);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const initialFilter: ApiSchemaFilter = { filterQueries: [], sortQuery: {} };
  const [filterData, setFilterData] = useState<ApiSchemaFilter>(initialFilter);
  const [filterDataChange, setFilterDataChange] = useState<number>(0);

  const { spokenTerms, spokenTermsCount, isLoading } =
    useFunctionalityLanguageSpokenTerms({
      pageSize,
      pageNo: pageIndex,
      searchText,
      setLoaderProgress,
      filterData,
      filterDataChange,
    });

  const callCenterId = useAppSelector(
    (state: VRIAppStateType) =>
      state.auth.menuSettings?.callCenterSettings.callCenterId
  );

  const spokenTermsTableColumns: TableColumnExtend<SpokenTermRow>[] = [
    {
      name: <ColumnToolTip data="Call SID" />,
      sortable: false,
      width: "300px",
      selector: (row: SpokenTermRow) => (
        <ColumnToolTip data={row.callSid ?? ""} />
      ),
    },
    {
      name: <ColumnToolTip data="Date" />,
      sortable: false,
      width: "180px",
      selector: (row: SpokenTermRow) => (
        <ColumnToolTip data={formatDateTimeOrDash(row.dateTime)} />
        
      ),
    },
    {
      name: <ColumnToolTip data="Spoken Terms" />,
      sortable: false,
      selector: (row: SpokenTermRow) => (
        <ColumnToolTip data={stripDateTime(row.spokenTerms ?? "")} />
      ),
    },
  ];


  useEffect(() => {
    setHasPagingData(spokenTermsCount > 0);
  }, [spokenTermsCount]);

  const handleFilter = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const columns: ApiSchemaColumn[] = [];
    let start: string | undefined;
    let end: string | undefined;

    if (startDate) {
      start = moment(startDate).startOf("day").format("YYYY-MM-DD[T]HH:mm:ss.SSS[Z]");
    }
    if (endDate) {
      end = moment(endDate).endOf("day").format("YYYY-MM-DD[T]HH:mm:ss.SSS[Z]");
    }

    if (start || end) {
      columns.push({
        columnName: "CreatedAt",
        dateQuery: { startDate: start, endDate: end },
      });
    }

    setFilterData((prev) => ({
      ...prev,
      filterQueries: columns,
    }));
    setFilterDataChange(Math.floor(Math.random() * 100) + 1);
    setPageIndex(1);
  };

  const handleResetFilter = () => {
    setStartDate(null);
    setEndDate(null);
    setFilterData(initialFilter);
    setFilterDataChange(Math.floor(Math.random() * 100) + 1);
    setPageIndex(1);
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
    setPageIndex(1);
  };

  const handlePageIndexChange = (index: number) => {
    setPageIndex(index);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setPageIndex(1);
  };

  const filterPanelContent = (
    <div className="col-12 collapse mt-3" id="spokenTermsFilterPanel">
      <div className="card card-body">
        <form onSubmit={handleFilter}>
          <label>Date</label>
          <div className="row">
            <div className="col-12 col-sm-6 form-group">
              <DatePicker
                dateFormat="MM/dd/yyyy"
                placeholderText="Start date"
                className="form-control"
                selected={startDate}
                onChange={(value) => setStartDate(value)}
                maxDate={endDate || undefined}
                isClearable
              />
            </div>
            <div className="col-12 col-sm-6 form-group">
              <DatePicker
                dateFormat="MM/dd/yyyy"
                placeholderText="End date"
                className="form-control"
                selected={endDate}
                onChange={(value) => setEndDate(value)}
                minDate={startDate || undefined}
                isClearable
              />
            </div>
          </div>
          <div className="row">
            <div className="col-12 text-right">
              <button
                type="button"
                className="btn btn-link text-uppercase filter-reset-btn"
                onClick={handleResetFilter}
              >
                Reset
              </button>
              <button type="submit" className="btn btn-primary text-uppercase mx-3">
                Filter
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );

  return (
    <>
      <LoadingBar
        progress={loaderProgress}
        onLoaderFinished={() => setLoaderProgress(0)}
      />

      <AdminPageTemplate
        pagingKey="SpokenTermsPerCall"
        loaderProgress={loaderProgress}
        showExportOptions={false}
        showFilter={true}
        filterPanel="spokenTermsFilterPanel"
        hideSearchBar={false}
        pageBreadCrumbTile="Call Center"
        pageTitle="Spoken Terms per Call"
        tableHeaders={spokenTermsTableColumns}
        tableData={spokenTerms}
        searchText={searchText}
        handleSearch={handleSearch}
        pageIndex={pageIndex}
        pageSize={pageSize}
        count={spokenTermsCount}
        loading={isLoading}
        hasPagingData={hasPagingData}
        setHasPagingData={setHasPagingData}
        onChangePageIndex={handlePageIndexChange}
        onChangePageSize={handlePageSizeChange}
        tableType={ExportTableType.CallCenterStatusLog}
        callCenterId={callCenterId}
        showAutoRefresh={false}
        isAccordionEnabled={false}
        noDataFoundText="No spoken terms to display."
        setFilterDataForAnyTable={setFilterData}
        setFilterDataChangeForAnyTable={setFilterDataChange}
        filterDataChange={filterDataChange}
        filterData={filterData}
        setLoaderProgress={setLoaderProgress}
        setIsShowCommonFilter={setIsShowFilter}
        isShowCommonFilter={isShowFilter}
        customFilterPanel={filterPanelContent}
      />
    </>
  );
}
